import { ParameterId as Api, SheetPath } from '@tableau/api-internal-contract-js';
import { ParameterId as PL } from '../../gen-src/preslayer/Params';
import { ActiveMarksDataConverter } from './ActiveMarksDataConverter';
import { ApiToPresLayerConverter } from './ApiToPresLayerConverter';
import { DataTableReaderConverter } from './DataTableReaderConverter';
import { ExportConverter } from './ExportConverter';
import { FiltersConverter } from './FiltersConverter';
import { MappingRegistry } from './MappingRegistry';
import { DashboardObjectPositionAndSizeUpdateConverter, ParametersDataConverter } from './ParametersDataConverter';
import { PresLayerToEmbeddingApiConverter } from './PresLayerToEmbeddingApiConverter';
import { PresLayerToExtensionsApiConverter } from './PresLayerToExtensionsApiConverter';
import { PresLayerToSharedApiConverter } from './PresLayerToSharedApiConverter';
import { DataScope, parseShowDataTablePresModel } from './ShowDataConverter';

/*tslint:disable-next-line */
const id = function (inParam: any): any {
  return inParam;
};

const invertBool = function (bool: boolean): boolean {
  return !bool;
};

/**
 * Factory class for creating parameters mappings based on the VersionNumber of the Api
 *
 * @export
 * @class ParameterMappingRegistryFactory
 */
export class ParameterMappingRegistryFactory {
  public static createPresLayerToApiParamRegistry(): PresLayerToApiMappingRegistry {
    const result = new PresLayerToApiMappingRegistry();

    result.addRegistration(PL.AddInLocator, Api.ExtensionLocator, PresLayerToExtensionsApiConverter.convertExtensionLocator);

    result.addRegistration(
      PL.AddInBootstrapInfo,
      Api.ExtensionBootstrapInfo,
      PresLayerToExtensionsApiConverter.convertExtensionBootstrapInfo,
    );

    result.addRegistration(
      PL.DataTablePresModel,
      Api.UnderlyingSummaryDataTable,
      parseShowDataTablePresModel.bind(undefined, DataScope.Summary),
    );

    result.addRegistration(PL.FieldCaption, Api.FieldName, id);
    result.addRegistration(PL.ParameterError, Api.ParameterError, id);
    result.addRegistration(PL.InvalidFields, Api.InvalidFields, id);
    result.addRegistration(PL.InvalidDates, Api.InvalidDates, id);
    result.addRegistration(PL.InvalidFieldCaption, Api.InvalidFieldCaption, id);
    result.addRegistration(PL.InvalidAggFieldName, Api.InvalidAggFieldName, id);
    result.addRegistration(PL.InvalidValues, Api.InvalidValues, id);
    result.addRegistration(PL.Field, Api.Field, PresLayerToSharedApiConverter.convertField);

    result.addRegistration(PL.SelectionData, Api.SelectedData, ActiveMarksDataConverter.buildSelectedDataTable);

    result.addRegistration(PL.HighlightedData, Api.HighlightedData, ActiveMarksDataConverter.buildHighlightedDataTable);

    result.addRegistration(PL.AddInSettingsInfo, Api.ExtensionSettingsInfo, PresLayerToExtensionsApiConverter.convertExtensionSettingsInfo);

    result.addRegistration(PL.Worksheet, Api.WorksheetName, id);

    result.addRegistration(PL.ParameterCtrls, Api.ParameterInfos, ParametersDataConverter.convertParameterList);

    result.addRegistration(PL.ParameterCtrl, Api.ParameterInfo, ParametersDataConverter.convertParameter);

    result.addRegistration(PL.DataSchema, Api.DataSchema, PresLayerToExtensionsApiConverter.convertDataSchema);

    result.addRegistration(PL.FiltersInfo, Api.WorksheetFilters, FiltersConverter.convertFiltersPresModel);

    result.addRegistration(PL.CategoricalDomain, Api.CategoricalDomain, FiltersConverter.convertCategoricalDomain);

    result.addRegistration(PL.QuantitativeDomain, Api.QuantitativeDomain, FiltersConverter.convertQuantitativeDomain);

    result.addRegistration(PL.ActiveTableInfos, Api.ActiveTablesInfo, PresLayerToExtensionsApiConverter.convertActiveTables);

    result.addRegistration(PL.JoinDescription, Api.JoinDescription, PresLayerToExtensionsApiConverter.convertJoinDescription);

    result.addRegistration(
      PL.ConnectionDescriptionSummaries,
      Api.ConnectionDescriptionSummaries,
      PresLayerToExtensionsApiConverter.convertConnectionDescrptionSummaries,
    );

    result.addRegistration(PL.ExtensionDialogPayload, Api.ExtensionDialogPayload, id);

    result.addRegistration(
      PL.ExtensionDialogResult,
      Api.ExtensionDialogResult,
      PresLayerToExtensionsApiConverter.convertExtensionDialogResult,
    );

    result.addRegistration(PL.DataSource, Api.DataSource, PresLayerToExtensionsApiConverter.convertDataSource);

    result.addRegistration(
      PL.ShowDataTableIdWithCaptionPresModelList,
      Api.LogicalTableInfoList,
      PresLayerToExtensionsApiConverter.convertShowDataTableObjectList,
    );

    result.addRegistration(
      PL.DataTablePresModel,
      Api.UnderlyingDataTable,
      parseShowDataTablePresModel.bind(undefined, DataScope.Underlying),
    );

    result.addRegistration(PL.VizAPISVG, Api.VizAPISVG, id);

    result.addRegistration(PL.ExternalMenuItemDisplayName, Api.ExternalMenuItemDisplayName, id);
    result.addRegistration(PL.ExternalMenuItemId, Api.ExternalMenuItemId, id);
    result.addRegistration(PL.ExternalMenuHeader, Api.ExternalMenuHeader, id);
    result.addRegistration(PL.ExternalMenuDescription, Api.ExternalMenuDescription, id);

    result.addRegistration(PL.FiltersInfo, Api.DashboardFilters, FiltersConverter.convertFiltersPresModel);

    result.addRegistration(PL.SharedFilterPresModel, Api.SharedFiltersInfo, PresLayerToEmbeddingApiConverter.convertSharedFilters);

    result.addRegistration(PL.EmbeddingCustomViewList, Api.CustomViews, PresLayerToEmbeddingApiConverter.convertCustomViews);
    result.addRegistration(PL.EmbeddingCustomView, Api.CustomView, PresLayerToEmbeddingApiConverter.convertCustomView);

    result.addRegistration(PL.EmbeddingStoryPoint, Api.StoryPointInfo, PresLayerToEmbeddingApiConverter.convertStoryPointPresModel);

    result.addRegistration(PL.URL, Api.Url, id);

    result.addRegistration(PL.AnnotationList, Api.AnnotationList, id);

    result.addRegistration(
      PL.EmbeddingApiBootstrap,
      Api.EmbeddingBootstrapInfo,
      PresLayerToEmbeddingApiConverter.convertEmbeddingBootstrap,
    );

    result.addRegistration(PL.DataTableReader, Api.DataTableReader, DataTableReaderConverter.convertDataTableReaderPresModel);

    result.addRegistration(PL.DataTableReaderPage, Api.DataTable, DataTableReaderConverter.convertDataTableReaderPagePresModel);

    result.addRegistration(PL.ExportFilePresModel, Api.ExportFile, id);
    result.addRegistration(PL.ExportPdfOptionsPresModel, Api.ExportPdfOptions, id);
    result.addRegistration(PL.ExportFilePresModel, Api.ExportFile, id);
    result.addRegistration(
      PL.ExportCrosstabOptionsDialogPresModel,
      Api.ExportCrosstabSheetMap,
      ExportConverter.convertCrosstabOptionsDialogToMap,
    );

    result.addRegistration(
      PL.EmbeddingAbstractQueryWithDataModel,
      Api.EmbeddingAbstractQuery,
      PresLayerToEmbeddingApiConverter.convertAbstractQuery,
    );

    result.addRegistration(PL.VizStateWithDataModel, Api.VizAPIStateWithDataModel, id);
    result.addRegistration(PL.VizStateWarningMsg, Api.VizAPIStateWarningMsg, id);
    result.addRegistration(PL.VisualSpecification, Api.VisualSpecification, PresLayerToSharedApiConverter.convertVisualSpecification);
    result.addRegistration(PL.TooltipText, Api.TooltipText, id);

    result.addRegistration(PL.MarksCardIndex, Api.MarksCardIndex, id);
    result.addRegistration(PL.EncodingType, Api.EncodingType, id);
    result.addRegistration(PL.StartIndex, Api.StartIndex, id);
    result.addRegistration(PL.Columns, Api.Columns, id);
    result.addRegistration(PL.FromIndex, Api.FromIndex, id);
    result.addRegistration(PL.ToIndex, Api.ToIndex, id);
    result.addRegistration(PL.DeleteCount, Api.DeleteCount, id);
    result.addRegistration(PL.FieldCount, Api.FieldCount, id);

    return result;
  }

  public static createApiToPresLayerParamRegistry(): ApiToPresLayerMappingRegistry {
    const result = new ApiToPresLayerMappingRegistry();

    result.addRegistration(Api.ExtensionLocator, PL.AddInLocator, ApiToPresLayerConverter.convertExtensionLocator);
    result.addRegistration(Api.SettingsValues, PL.AddInSettings, id);
    result.addRegistration(Api.IgnoreAliases, PL.IgnoreAliases, id);
    result.addRegistration(Api.IgnoreAliases, PL.ShowAliases, invertBool);
    result.addRegistration(Api.IgnoreSelection, PL.IgnoreSelection, id);
    result.addRegistration(Api.ApplyWorksheetFormatting, PL.ApplyFormatting, id);
    result.addRegistration(Api.IncludeAllColumns, PL.IncludeAllColumns, id);

    /**
     * Note that historically we made unfortunate name choices. Inside Tableau, the fields have names and captions.
     * In the extensions API, we call these id and name.
     * The mapping from external to internal is then:
     * field id --> field name
     * field name --> field caption
     */
    result.addRegistration(Api.ColumnsToIncludeById, PL.ColumnFullNames, id);
    result.addRegistration(Api.ColumnsToIncludeById, PL.Columns, id);
    result.addRegistration(Api.ColumnsToInclude, PL.FieldCaptions, id);

    result.addRegistration(Api.MaxRows, PL.MaxRows, id);
    result.addRegistration(Api.LogicalTableId, PL.ShowDataTableId, id);
    result.addRegistration(Api.VisualId, PL.VisualIDPM, ApiToPresLayerConverter.convertVisualId);
    result.addRegistration(Api.ShowDataTableFormat, PL.ShowDataTableFormat, ApiToPresLayerConverter.convertShowDataTableFormat);
    result.addRegistration(Api.EncodingType, PL.EncodingType, id);

    result.addRegistration(Api.FieldName, PL.QualifiedFieldCaption, id);
    result.addRegistration(Api.FilterValues, PL.FilterAliases, ApiToPresLayerConverter.convertFilterValues);
    result.addRegistration(Api.FilterUpdateType, PL.FilterUpdateType, ApiToPresLayerConverter.convertFilterType);
    result.addRegistration(Api.IsExcludeMode, PL.Exclude, id);
    result.addRegistration(Api.FilterRangeMin, PL.RangeMin, id);
    result.addRegistration(Api.FilterRangeMax, PL.RangeMax, id);
    result.addRegistration(Api.FilterRangeNullOption, PL.Included, ApiToPresLayerConverter.convertNullOption);
    result.addRegistration(Api.FieldId, PL.FieldName, id);
    result.addRegistration(Api.FieldId, PL.FieldNameString, id);
    result.addRegistration(Api.DomainType, PL.FilterDomainType, ApiToPresLayerConverter.convertFilterDomainType);
    result.addRegistration(Api.AnchorDate, PL.AnchorDate, id);
    result.addRegistration(Api.PeriodType, PL.DatePeriodType, ApiToPresLayerConverter.convertPeriodType);
    result.addRegistration(Api.DateRangeType, PL.DateRangeType, ApiToPresLayerConverter.convertDateRangeType);
    result.addRegistration(Api.RangeN, PL.RangeN, id);

    result.addRegistration(Api.ParameterCaption, PL.FieldCaption, id);
    result.addRegistration(Api.ParameterFieldName, PL.FieldNameString, id);
    result.addRegistration(Api.ParameterFieldName, PL.GlobalFieldName, id);
    result.addRegistration(Api.ParameterValue, PL.ValueStr, id);

    result.addRegistration(Api.SheetPath, PL.SheetPathPM, (pm) => ApiToPresLayerConverter.convertSheetPath(pm as SheetPath));

    result.addRegistration(Api.Selection, PL.Selection, id);
    result.addRegistration(Api.SelectionUpdateType, PL.SelectionUpdateType, id);
    result.addRegistration(Api.HierValSelectionModels, PL.HierValSelectionModels, id);
    result.addRegistration(Api.QuantRangeSelectionModels, PL.QuantRangeSelectionModels, id);
    result.addRegistration(Api.DimValSelectionModels, PL.DimValSelectionModels, id);

    // The param PL.Datasource is the ID string for a specific datasource, not to be confused
    // with the param PL.DataSource, which is the full pres model for a data source.
    result.addRegistration(Api.DataSourceId, PL.Datasource, id);
    result.addRegistration(Api.DataSourceId, PL.DatasourceId, id);
    result.addRegistration(Api.DeltaTimeMs, PL.DeltaTimeMs, id);
    result.addRegistration(Api.ShouldRefreshDS, PL.ShouldRefreshDS, id);

    result.addRegistration(Api.ExtensionDialogUrl, PL.ExtensionDialogUrl, id);
    result.addRegistration(Api.ExtensionDialogPayload, PL.ExtensionDialogPayload, id);
    result.addRegistration(Api.ExtensionDialogStyle, PL.ExtensionDialogStyle, id);
    result.addRegistration(Api.ExtensionDialogH, PL.ExtensionDialogH, id);
    result.addRegistration(Api.ExtensionDialogW, PL.ExtensionDialogW, id);

    result.addRegistration(Api.ExtensionContextMenuIds, PL.ExtensionContextMenuIds, id);
    result.addRegistration(Api.IsExtensionDialog, PL.IsExtensionDialog, id);

    result.addRegistration(Api.Dashboard, PL.Dashboard, id);
    result.addRegistration(Api.ZoneIdsVisibilityMap, PL.ZoneIdsVisibilityMap, id);
    result.addRegistration(
      Api.DashboardObjectPositionAndSizeUpdateList,
      PL.ZonePositionAndSizeUpdateList,
      DashboardObjectPositionAndSizeUpdateConverter.convertToZonePositionAndSizeUpdatePresModelArray,
    );

    result.addRegistration(Api.ReplaySpeed, PL.ReplaySpeed, id);

    result.addRegistration(Api.VizAPIInputJson, PL.VizAPIInputJson, id);
    result.addRegistration(Api.ExtensionZoneId, PL.ZoneID, id);
    result.addRegistration(Api.ClickThroughEnabled, PL.PointerEventsShouldBeSetToAuto, ApiToPresLayerConverter.convertClickThroughEnabled);
    result.addRegistration(Api.ClickThroughEnabled, PL.IsTransparentForMouseEvents, id);

    result.addRegistration(Api.WorksheetName, PL.Worksheet, id);
    result.addRegistration(Api.ExternalMenuItemDisplayName, PL.ExternalMenuItemDisplayName, id);
    result.addRegistration(Api.ExternalMenuItemId, PL.ExternalMenuItemId, id);
    result.addRegistration(Api.ExternalMenuHeader, PL.ExternalMenuHeader, id);
    result.addRegistration(Api.ExternalMenuDescription, PL.ExternalMenuDescription, id);

    result.addRegistration(Api.State, PL.State, id);

    result.addRegistration(Api.SwitchToSheetName, PL.TargetSheet, id);

    result.addRegistration(Api.VisualId, PL.Worksheet, ApiToPresLayerConverter.convertVisualIdToWorksheetName);

    result.addRegistration(Api.SharedFilterSheets, PL.Sheets, id);

    result.addRegistration(Api.FilterLevels, PL.FilterLevels, id);

    result.addRegistration(Api.CustomViewName, PL.CustomViewName, id);
    result.addRegistration(Api.CustomViewLuid, PL.CustomViewLuid, id);
    result.addRegistration(Api.CustomViewIsDefault, PL.CustomViewIsDefault, id);
    result.addRegistration(Api.CustomViewIsPublic, PL.CustomViewIsPublic, id);

    result.addRegistration(Api.StoryPointIndex, PL.Index, id);
    result.addRegistration(Api.SheetName, PL.Sheet, id);
    result.addRegistration(Api.MinHeight, PL.MinHeight, id);
    result.addRegistration(Api.MaxHeight, PL.MaxHeight, id);
    result.addRegistration(Api.MinWidth, PL.MinWidth, id);
    result.addRegistration(Api.MaxWidth, PL.MaxWidth, id);

    result.addRegistration(Api.AnnotateEnum, PL.AnnotateEnum, id);
    result.addRegistration(Api.TargetPoint, PL.TargetPt, id);
    result.addRegistration(Api.SelectionList, PL.SelectionList, id);
    result.addRegistration(Api.Text, PL.FormattedText, id);

    result.addRegistration(Api.ViewDataTableCacheId, PL.UUID, id);
    result.addRegistration(Api.ViewDataTableId, PL.ViewDataTableId, id);
    result.addRegistration(Api.PageRowCount, PL.URowCount, id);
    result.addRegistration(Api.DataRowIndex, PL.RowIndex, id);

    result.addRegistration(Api.SheetIdentifier, PL.SheetSimpleID, id);
    result.addRegistration(Api.SendNotifications, PL.SendNotifications, id);
    result.addRegistration(Api.UseTabDelimiters, PL.UseTabs, id);
    result.addRegistration(Api.CurrentSheetType, PL.SheetType, id);
    result.addRegistration(Api.ExportPdfOptions, PL.ExportPdfOptionsPresModel, id);
    result.addRegistration(Api.ExportOriginUrl, PL.ExportOriginURL, id);
    result.addRegistration(Api.SelectedSheetNames, PL.SelectedSheetIDs, id);

    result.addRegistration(Api.VizAPIStateWithDataModel, PL.VizStateWithDataModel, id);
    result.addRegistration(Api.VisualSpecification, PL.VisualSpecification, id);

    result.addRegistration(Api.HoverTupleInteraction, PL.HoverTupleInteraction, ApiToPresLayerConverter.convertHoverTupleInteraction);
    result.addRegistration(Api.SelectTuplesInteraction, PL.SelectTuplesInteraction, ApiToPresLayerConverter.convertSelectTuplesInteraction);
    result.addRegistration(Api.TooltipTextRequest, PL.TooltipTextRequest, ApiToPresLayerConverter.convertTooltipTextRequest);

    result.addRegistration(Api.MarksCardIndex, PL.MarksCardIndex, id);
    result.addRegistration(Api.EncodingType, PL.EncodingType, id);
    result.addRegistration(Api.StartIndex, PL.StartIndex, id);
    result.addRegistration(Api.Columns, PL.Columns, id);
    result.addRegistration(Api.FromIndex, PL.FromIndex, id);
    result.addRegistration(Api.ToIndex, PL.ToIndex, id);
    result.addRegistration(Api.DeleteCount, PL.DeleteCount, id);
    result.addRegistration(Api.FieldCount, PL.FieldCount, id);

    return result;
  }
}

// TODO TFSID 1106239 simplify api-platform mapping registration system
export class ApiToPresLayerMappingRegistry
  // tslint:disable-next-line:no-any
  extends MappingRegistry<Api, PL, (apiModel: any) => any> {}

export class PresLayerToApiMappingRegistry
  // tslint:disable-next-line:no-any
  extends MappingRegistry<PL, Api, (presLayerPm: any) => any> {}
